"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ALERT_LABELS, type AlertType } from "@/lib/sentiment";

// ── Types ──────────────────────────────────────────────────────────────────

export interface Flow {
  id: string;
  org_id: string;
  name: string;
  greeting_message: string | null;
  min_score_for_cpa: number;
  active: boolean;
  created_at: string;
}

export interface UploadedDoc {
  filename: string;
  uploaded_at: string;
}

export interface MemoryBank {
  id: string;
  flow_id: string;
  company_description: string | null;
  products_services: string | null;
  pricing: string | null;
  icp: string | null;
  knowledge_urls: string[];
  faqs: string | null;
  objections: string | null;
  policies: string | null;
  communication_tone: string | null;
  uploaded_docs: UploadedDoc[];
}

export interface QuestionOption {
  id: string;
  concept_label: string;
  score_value: number;
  semantic_aliases: string[];
  tag_value?: string | null;
}

export interface Question {
  id: string;
  flow_id: string;
  order_index: number;
  question_text: string;
  question_type: string;
  max_retries: number;
  is_informative: boolean;
  tag_key?: string | null;
  options: QuestionOption[];
}

export interface LeadTag {
  key: string;
  value: string;
  source: string;
}

export interface AttributionTouch {
  session_id?: string | null;
  occurred_at: string;
  canal?: string | null;
  campana?: string | null;
  pagina?: string | null;
}

export interface MetaReferralSummary {
  ad_id?: string | null;
  ad_name?: string | null;
  campaign_name?: string | null;
  enrichment_status: string;
  occurred_at: string;
}

export interface Lead {
  is_demo: boolean;
  id: string;
  org_id: string;
  flow_id: string | null;
  flow_name: string | null;
  name: string | null;
  phone: string;
  email: string | null;
  company: string | null;
  campaign: string | null;
  origin: string | null;
  origin_channel: string | null;
  origin_campaign: string | null;
  origin_page: string | null;
  mecanismo?: string | null;
  canal?: string | null;
  campana?: string | null;
  via?: string | null;
  referido_por?: string | null;
  primera_fuente?: AttributionTouch | null;
  ultima_fuente?: AttributionTouch | null;
  meta_referral?: MetaReferralSummary | null;
  primera_pagina?: string | null;
  fecha_primera_visita?: string | null;
  lead_type: string | null;
  total_score: number;
  max_possible_score: number;
  agent_state: string | null;
  agent_disabled: boolean;
  pipeline_status: string;
  pipeline_deal_id?: string | null;
  pipeline_stage_name?: string | null;
  pipeline_stage_kind?: string | null;
  pipeline_loss_reason_id?: string | null;
  pipeline_loss_reason_text?: string | null;
  assigned_team_id: string | null;
  crm_contact_id?: string | null;
  crm_company_id?: string | null;
  crm_deal_id?: string | null;
  crm_pipeline_stage?: string | null;
  crm_pipeline_stage_id?: string | null;
  crm_pushed_at?: string | null;
  crm_push_status?: string | null;
  crm_push_error?: string | null;
  created_at: string;
  updated_at?: string | null;
  sentiment: string | null;
  last_contact_at: string | null;
  active_alert_type: string | null;
  conversation_id?: string | null;
  conversation_state?: string | null;
  current_question_index?: number | null;
  current_question_text?: string | null;
  first_contact_at?: string | null;
  assigned_team_name?: string | null;
  deal_stages?: {
    deal_id: string;
    title?: string | null;
    stage_name: string;
    stage_kind: string;
    archived?: boolean;
    loss_reason_text?: string | null;
    deal_created_at: string;
  }[];
  tags?: LeadTag[];
}

export interface OrgSettings {
  id: string;
  name: string;
  slug: string;
  plan: string | null;
  kind: string;
  team_visibility: "restricted_by_team" | "global" | string;
  form_webhook_token: string | null;
  origin_agent_color: string;
  origin_form_color: string;
  origin_manual_color: string;
  web_tracking_enabled: boolean;
  meta_attribution_enabled: boolean;
  communication_consent_required: boolean;
  active: boolean;
  created_at: string;
}

export interface OrgMember {
  id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "viewer" | string;
  is_active: boolean;
  created_at: string;
}

export interface PlatformTenant {
  org_id: string;
  org_name: string;
  org_slug: string;
  kind: string;
  active: boolean;
  users_count: number;
  admins_count: number;
  admin_email: string | null;
  created_at: string;
}

export interface LeadList {
  items: Lead[];
  total: number;
  page: number;
  page_size: number;
}

export interface ContactDealSummary {
  id: string;
  principal_contact_id: string;
  stage_id: string;
  source: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  direction: string;
  sender_type: string;
  content: string | null;
  message_type: string;
  sent_at: string;
}

export { ALERT_LABELS, type AlertType };

export interface Conversation {
  is_demo: boolean;
  id: string;
  lead_id: string;
  state: string;
  current_question_index: number;
  human_operator_id: string | null;
  last_message_at: string | null;
  created_at: string;
  lead_name: string | null;
  lead_phone: string | null;
  lead_email: string | null;
  lead_company: string | null;
  lead_created_at: string | null;
  lead_type: string | null;
  agent_state: string | null;
  total_score: number | null;
  max_possible_score: number | null;
  sentiment: "positive" | "negative" | "neutral" | null;
  handoff_summary: string | null;
  flow_id: string | null;
  flow_name: string | null;
  campaign: string | null;
  origin: string | null;
  // Sentiment system
  sentiment_state: string | null;
  active_alert_type: AlertType | null;
  active_alert_id: string | null;
  active_alert_channel: string | null;
  active_alert_availability: string | null;
  has_active_alert: boolean;
  opted_out: boolean;
  messages?: Message[];
  contact?: Lead | null;
}

export interface ConversationList {
  items: Conversation[];
  total: number;
  page: number;
  page_size: number;
}

export interface DashboardStats {
  total_conversations: number;
  qualified: number;
  not_qualified: number;
  incomplete_active: number;
  incomplete_closed: number;
  duplicate: number;
  technical_error: number;
  incomplete: number;
  active_alerts: number;
  conversion_rate: number;
  breakdown: { lead_type: string; count: number; percentage: number }[];
  daily_series: { date: string; conversations: number; qualified: number }[];
}

export interface WhatsAppStatus {
  connected: boolean;
  phone_display: string | null;
  waba_id: string | null;
  phone_number_id: string | null;
}

export interface MessagingStatus {
  provider: string;
  connected: boolean;
  phone_display: string | null;
  phone_number_id: string | null;
  flow_id: string | null;
  flow_name: string | null;
  waba_id: string | null;
  waba_business_name: string | null;
  connection_method: string | null;
  app_subscribed: boolean;
  webhook_verify_token: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  last_error: string | null;
  embedded_signup_available: boolean;
  embedded_signup_config_id: string | null;
  embedded_signup_app_id: string | null;
}

export interface MessagingConnection extends MessagingStatus {
  id: string;
}

export interface MessagingTestResponse {
  sent: boolean;
  provider_message_id: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface WhatsAppTemplate {
  id: string;
  org_id: string;
  flow_id: string | null;
  name: string;
  category: "MARKETING" | "UTILITY" | string;
  language: string;
  header_text: string | null;
  body_text: string;
  footer_text: string | null;
  quick_reply_buttons: string[];
  wa_template_id: string | null;
  rejection_reason: string | null;
  status: "pending" | "approved" | "rejected" | "paused" | string;
  created_at: string;
  updated_at: string | null;
}

export interface WhatsAppTemplateCreate {
  name: string;
  category: "MARKETING" | "UTILITY";
  language: string;
  header_text?: string | null;
  body_text: string;
  footer_text?: string | null;
  quick_reply_buttons: string[];
  flow_id?: string | null;
}

// ── Flows ──────────────────────────────────────────────────────────────────

export function useFlows() {
  return useQuery<Flow[]>({
    queryKey: ["flows"],
    queryFn: () => api.get("/flows"),
  });
}

export function useFlow(id: string) {
  return useQuery<Flow & { memory_bank?: MemoryBank; questions?: Question[] }>({
    queryKey: ["flows", id],
    queryFn: () => api.get(`/flows/${id}`),
    enabled: !!id,
  });
}

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; greeting_message?: string }) =>
      api.post<Flow>("/flows", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flows"] }),
  });
}

export function useUpdateFlow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Flow>) => api.put<Flow>(`/flows/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows"] });
      qc.invalidateQueries({ queryKey: ["flows", id] });
    },
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/flows/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flows"] }),
  });
}

export function useDuplicateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, name }: { flowId: string; name: string }) =>
      api.post<Flow>(`/flows/${flowId}/duplicate`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flows"] }),
  });
}

// ── Memory Bank ────────────────────────────────────────────────────────────

export function useMemoryBank(flowId: string) {
  return useQuery<MemoryBank>({
    queryKey: ["memory-bank", flowId],
    queryFn: () => api.get(`/flows/${flowId}/memory-bank`),
    enabled: !!flowId,
  });
}

export function useUpdateMemoryBank(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<MemoryBank>) =>
      api.put<MemoryBank>(`/flows/${flowId}/memory-bank`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory-bank", flowId] }),
  });
}

export function useUploadMemoryBankDoc(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.upload<MemoryBank>(`/flows/${flowId}/memory-bank/upload-doc`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory-bank", flowId] }),
  });
}

export function useDeleteMemoryBankDoc(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docIndex: number) =>
      api.delete<MemoryBank>(`/flows/${flowId}/memory-bank/docs/${docIndex}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory-bank", flowId] }),
  });
}

// ── Questions ──────────────────────────────────────────────────────────────

export function useQuestions(flowId: string) {
  return useQuery<Question[]>({
    queryKey: ["questions", flowId],
    queryFn: () => api.get(`/flows/${flowId}/questions`),
    enabled: !!flowId,
  });
}

export function useCreateQuestion(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Question, "id" | "flow_id" | "order_index">) =>
      api.post<Question>(`/flows/${flowId}/questions`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

export function useDeleteQuestion(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) =>
      api.delete(`/flows/${flowId}/questions/${questionId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

export function useUpdateQuestion(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, data }: { questionId: string; data: Partial<Question> & { options?: Omit<QuestionOption, 'id'>[] } }) =>
      api.put<Question>(`/flows/${flowId}/questions/${questionId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

export function useReorderQuestions(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionIds: string[]) =>
      api.post<Question[]>(`/flows/${flowId}/questions/reorder`, { question_ids: questionIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

// ── CPA Config ─────────────────────────────────────────────────────────────

export interface CPAConfig {
  id: string;
  flow_id: string;
  cpa_type: string | null;
  cpa_link: string | null;
  cpa_launch_text: string | null;
  min_score: number | null;
}

export function useCPAConfig(flowId: string) {
  return useQuery<CPAConfig>({
    queryKey: ["cpa", flowId],
    queryFn: () => api.get(`/flows/${flowId}/cpa`),
    enabled: !!flowId,
  });
}

export function useUpdateCPAConfig(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CPAConfig>) => api.put<CPAConfig>(`/flows/${flowId}/cpa`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cpa", flowId] }),
  });
}

// ── Reactivation Config ────────────────────────────────────────────────────

export interface ReactivationConfig {
  id: string;
  flow_id: string;
  incomplete_hours: number | null;
  incomplete_max_attempts: number | null;
  incomplete_interval_hours: number | null;
  incomplete_message: string | null;
  business_hours_start: string | null;
  business_hours_end: string | null;
  timezone: string | null;
  incomplete_template_id: string | null;
}

export function useReactivationConfig(flowId: string) {
  return useQuery<ReactivationConfig>({
    queryKey: ["reactivation", flowId],
    queryFn: () => api.get(`/flows/${flowId}/reactivation`),
    enabled: !!flowId,
  });
}

export function useUpdateReactivationConfig(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ReactivationConfig>) =>
      api.put<ReactivationConfig>(`/flows/${flowId}/reactivation`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reactivation", flowId] }),
  });
}

// ── Closure Config ─────────────────────────────────────────────────────────

export interface ClosureConfig {
  id: string;
  flow_id: string;
  not_qualified_close_message: string | null;
}

export function useClosureConfig(flowId: string) {
  return useQuery<ClosureConfig>({
    queryKey: ["closures", flowId],
    queryFn: () => api.get(`/flows/${flowId}/closures`),
    enabled: !!flowId,
  });
}

export function useUpdateClosureConfig(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ClosureConfig>) => api.put<ClosureConfig>(`/flows/${flowId}/closures`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["closures", flowId] }),
  });
}

// ── Leads ──────────────────────────────────────────────────────────────────

export function useLeads(params?: {
  mode?: "real" | "demo";
  page?: number;
  lead_type?: string;
  agent_state?: string;
  flow_id?: string;
  campaign?: string;
  search?: string;
  sentiment?: string;
  tag?: string[];
}) {
  const mode = params?.mode ?? "real";
  const q = new URLSearchParams({ mode });
  if (params?.page) q.set("page", String(params.page));
  if (params?.lead_type) q.set("lead_type", params.lead_type);
  if (params?.agent_state) q.set("agent_state", params.agent_state);
  if (params?.flow_id) q.set("flow_id", params.flow_id);
  if (params?.campaign) q.set("campaign", params.campaign);
  if (params?.search) q.set("search", params.search);
  if (params?.sentiment) q.set("sentiment", params.sentiment);
  params?.tag?.forEach((tag) => q.append("tag", tag));
  const qs = q.toString();

  return useQuery<LeadList>({
    queryKey: ["leads", { ...params, mode }],
    queryFn: () => api.get(`/leads${qs ? `?${qs}` : ""}`),
  });
}

export function useLead(id: string) {
  return useQuery<Lead>({
    queryKey: ["leads", id],
    queryFn: () => api.get(`/leads/${id}`),
    enabled: !!id,
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (leadId: string) => api.delete(`/leads/${leadId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

export function usePromoteContact(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation<ContactDealSummary>({
    mutationFn: () => api.post(`/contacts/${contactId}/promote`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["pipeline-deals"] });
    },
  });
}

export function useUpsertLeadTag(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation<LeadTag, Error, { key: string; value: string }>({
    mutationFn: (data) => api.post(`/leads/${leadId}/tags`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["leads", leadId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useDeleteLeadTag(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (key) => api.delete(`/leads/${leadId}/tags/${encodeURIComponent(key)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["leads", leadId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

// ── Conversations ──────────────────────────────────────────────────────────

export function useConversations(params?: {
  mode?: "real" | "demo";
  state?: string;
  search?: string;
  lead_id?: string;
  lead_type?: string;
  agent_state?: string;
  flow_id?: string;
  campaign?: string;
  sentiment?: string;
  tag?: string[];
}) {
  const mode = params?.mode ?? "real";
  const q = new URLSearchParams({ mode });
  if (params?.state) q.set("state", params.state);
  if (params?.search) q.set("search", params.search);
  if (params?.lead_id) q.set("lead_id", params.lead_id);
  if (params?.lead_type) q.set("lead_type", params.lead_type);
  if (params?.agent_state) q.set("agent_state", params.agent_state);
  if (params?.flow_id) q.set("flow_id", params.flow_id);
  if (params?.campaign) q.set("campaign", params.campaign);
  if (params?.sentiment) q.set("sentiment", params.sentiment);
  params?.tag?.forEach((tag) => q.append("tag", tag));
  const qs = q.toString();

  return useQuery<ConversationList>({
    queryKey: ["conversations", { ...params, mode }],
    queryFn: () => api.get(`/conversations${qs ? `?${qs}` : ""}`),
    refetchInterval: 15000,
  });
}

export function useConversation(id: string) {
  return useQuery<Conversation>({
    queryKey: ["conversations", id],
    queryFn: () => api.get(`/conversations/${id}`),
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useTakeControl(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/conversations/${conversationId}/take-control`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useReleaseControl(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/conversations/${conversationId}/release-control`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useSendMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      api.post(`/conversations/${conversationId}/messages`, { content }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

// ── Alerts ─────────────────────────────────────────────────────────────────

export interface Alert {
  id: string;
  alert_type: string;
  alert_label: string;
  priority: number;
  required_action: string;
  status: string;
  lead_id: string;
  lead_name: string | null;
  lead_phone: string | null;
  lead_company: string | null;
  lead_type: string | null;
  opted_out: boolean;
  conversation_id: string | null;
  deal_id: string | null;
  flow_id: string | null;
  flow_name: string | null;
  campaign: string | null;
  origin: string | null;
  preferred_channel: string | null;
  availability: string | null;
  trigger_message: string | null;
  created_at: string;
  resolved_at: string | null;
  reviewed_by_name: string | null;
}

export interface AlertList {
  items: Alert[];
  total: number;
  page: number;
  page_size: number;
}

export function useAlerts(params?: {
  alert_type?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  flow_id?: string;
  campaign?: string;
  page?: number;
}) {
  const q = new URLSearchParams();
  if (params?.alert_type) q.set("alert_type", params.alert_type);
  if (params?.status) q.set("status", params.status);
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  if (params?.flow_id) q.set("flow_id", params.flow_id);
  if (params?.campaign) q.set("campaign", params.campaign);
  if (params?.page) q.set("page", String(params.page));
  const qs = q.toString();

  return useQuery<AlertList>({
    queryKey: ["alerts", params],
    queryFn: () => api.get(`/alerts${qs ? `?${qs}` : ""}`),
    refetchInterval: 20000,
  });
}

export function useLeadAlerts(leadId: string | null | undefined) {
  return useQuery<AlertList>({
    queryKey: ["alerts", { lead_id: leadId }],
    queryFn: () => api.get(`/alerts?lead_id=${leadId}&status=&page_size=20`),
    enabled: !!leadId,
  });
}

export function usePendingAlertsCount() {
  return useQuery<{ count: number }>({
    queryKey: ["alerts-count"],
    queryFn: () => api.get("/alerts/pending-count"),
    refetchInterval: 20000,
  });
}

export function useReviewAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => api.post(`/alerts/${alertId}/review`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alerts-count"] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export function useDashboard() {
  return useQuery<DashboardStats>({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard/stats"),
    refetchInterval: 30000,
  });
}

// ── WhatsApp ───────────────────────────────────────────────────────────────

export function useWhatsAppStatus() {
  return useQuery<WhatsAppStatus>({
    queryKey: ["whatsapp-status"],
    queryFn: () => api.get("/whatsapp/status"),
  });
}

export function useConnectWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      waba_id: string;
      phone_number_id: string;
      access_token: string;
      phone_display?: string;
    }) => api.post("/whatsapp/connect", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-status"] });
      qc.invalidateQueries({ queryKey: ["messaging-status"] });
    },
  });
}

// ── Messaging integration (preferred over /whatsapp/* endpoints) ──────────

export function useMessagingStatus() {
  return useQuery<MessagingStatus>({
    queryKey: ["messaging-status"],
    queryFn: () => api.get("/integrations/messaging"),
  });
}

export function useMessagingConnections() {
  return useQuery<MessagingConnection[]>({
    queryKey: ["messaging-connections"],
    queryFn: () => api.get("/integrations/messaging/connections"),
  });
}

export function useConnectMessaging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      waba_id: string;
      phone_number_id: string;
      access_token: string;
      phone_display?: string;
      flow_id?: string;
    }) => api.post("/integrations/messaging/whatsapp/connect", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messaging-status"] });
      qc.invalidateQueries({ queryKey: ["messaging-connections"] });
      qc.invalidateQueries({ queryKey: ["whatsapp-status"] });
    },
  });
}

export function useDisconnectMessaging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flowId?: string | null) =>
      api.delete(`/integrations/messaging${flowId ? `?flow_id=${encodeURIComponent(flowId)}` : ""}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messaging-status"] });
      qc.invalidateQueries({ queryKey: ["messaging-connections"] });
      qc.invalidateQueries({ queryKey: ["whatsapp-status"] });
    },
  });
}

export function useTestMessaging() {
  return useMutation<MessagingTestResponse, unknown, { to: string; text?: string; flow_id?: string }>({
    mutationFn: (data) => api.post("/integrations/messaging/test", data),
  });
}

// ── WhatsApp templates ────────────────────────────────────────────────────

export function useWhatsAppTemplates(status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return useQuery<WhatsAppTemplate[]>({
    queryKey: ["whatsapp-templates", status ?? "all"],
    queryFn: () => api.get(`/templates${qs}`),
  });
}

export function useCreateWhatsAppTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: WhatsAppTemplateCreate) => api.post<WhatsAppTemplate>("/templates", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-templates"] });
    },
  });
}

export function useRefreshWhatsAppTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      api.post<WhatsAppTemplate>(`/templates/${templateId}/refresh-status`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-templates"] });
    },
  });
}

export function useDeleteWhatsAppTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.delete(`/templates/${templateId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-templates"] });
      qc.invalidateQueries({ queryKey: ["reactivation"] });
    },
  });
}

// ── CRM integrations ───────────────────────────────────────────────────────

export interface CRMStatus {
  connected: boolean;
  provider?: string | null;
  hub_id?: string | null;
  pipeline_id?: string | null;
  deal_stage_id?: string | null;
  last_sync_at?: string | null;
  last_error?: string | null;
}

export interface CRMDealStage {
  id: string;
  label: string;
  display_order: number;
}

export interface CRMDealPipeline {
  id: string;
  label: string;
  display_order: number;
  stages: CRMDealStage[];
}

export function useCRMStatus() {
  return useQuery<CRMStatus>({
    queryKey: ["crm-status"],
    queryFn: () => api.get("/integrations/crm"),
  });
}

export function useCRMPipelines(enabled: boolean) {
  return useQuery<{ pipelines: CRMDealPipeline[] }>({
    queryKey: ["crm-pipelines"],
    queryFn: () => api.get("/integrations/crm/pipelines"),
    enabled,
  });
}

export function useUpdateCRMConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Pick<CRMStatus, "pipeline_id" | "deal_stage_id">) =>
      api.patch<CRMStatus>("/integrations/crm", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-status"] }),
  });
}

export function useStartHubSpotOAuth() {
  return useMutation({
    mutationFn: () => api.get<{ authorize_url: string }>("/integrations/crm/hubspot/oauth/start"),
  });
}

export function useDisconnectCRM() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete("/integrations/crm"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-status"] }),
  });
}

export function useTestCRMConnection() {
  return useMutation({
    mutationFn: () => api.post("/integrations/crm/test", {}),
  });
}

export function usePushLeadToCRM(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean = false) =>
      api.post(`/leads/${leadId}/push-to-crm?force=${force}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
}

export function useDisableAgent(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Lead>(`/leads/${leadId}/disable-agent`, { confirm: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["leads", leadId] });
    },
  });
}

export function useEnableAgent(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Lead>(`/leads/${leadId}/enable-agent`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["leads", leadId] });
    },
  });
}

export function useUpdatePipelineStatus(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pipeline_status: string) =>
      api.patch<Lead>(`/leads/${leadId}/pipeline-status`, { pipeline_status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["leads", leadId] });
    },
  });
}

// ── Internal Pipeline ────────────────────────────────────────────────────────

export interface PipelineStage {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  kind: "opportunity" | "won" | "lost" | "custom" | string;
  position: number;
  is_fixed: boolean;
  created_at: string;
  updated_at?: string | null;
}

export interface LossReason {
  id: string;
  org_id: string;
  label: string;
  active: boolean;
  created_at: string;
}

export interface PipelineNote {
  id: string;
  deal_id: string;
  user_id: string | null;
  author_name?: string | null;
  author_email?: string | null;
  content: string;
  pinned: boolean;
  created_at: string;
  updated_at?: string | null;
}

export interface PipelineHistory {
  id: string;
  deal_id: string;
  from_stage_id: string | null;
  from_stage_name?: string | null;
  to_stage_id: string;
  to_stage_name?: string | null;
  user_id: string | null;
  user_name?: string | null;
  loss_reason_id: string | null;
  loss_reason_text: string | null;
  changed_at: string;
}

export interface PipelineDeal {
  id: string;
  org_id: string;
  lead_id: string;
  stage_id: string;
  stage_name: string;
  stage_slug: string;
  stage_kind: string;
  assigned_team_id: string | null;
  assigned_team_name?: string | null;
  source: string;
  archived: boolean;
  loss_reason_id: string | null;
  loss_reason_text: string | null;
  created_at: string;
  updated_at?: string | null;
  closed_at?: string | null;
  follow_up_at?: string | null;
  follow_up_note?: string | null;
  follow_up_alerted_at?: string | null;
  lead: Lead;
  tags: LeadTag[];
  notes: PipelineNote[];
  history: PipelineHistory[];
}

export interface PipelineDealList {
  items: PipelineDeal[];
  total: number;
  page: number;
  page_size: number;
}

export function useOrgSettings() {
  return useQuery<OrgSettings>({
    queryKey: ["org-settings"],
    queryFn: () => api.get("/org/settings"),
  });
}

export function useOrgMembers() {
  return useQuery<OrgMember[]>({
    queryKey: ["org-members"],
    queryFn: () => api.get("/org/members"),
  });
}

export function useInviteOrgMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; full_name: string; role: "admin" | "viewer"; password: string }) =>
      api.post<OrgMember>("/org/members/invite", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-members"] });
      qc.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useUpdateOrgSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Pick<OrgSettings,
      "team_visibility" | "origin_agent_color" | "origin_form_color" | "origin_manual_color" |
      "web_tracking_enabled" | "meta_attribution_enabled" | "communication_consent_required"
    >>) =>
      api.patch<OrgSettings>("/org/settings", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-settings"] }),
  });
}

export function usePipelineStages() {
  return useQuery<PipelineStage[]>({
    queryKey: ["pipeline-stages"],
    queryFn: () => api.get("/pipeline/stages"),
  });
}

export function useCreatePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => api.post<PipelineStage>("/pipeline/stages", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline-stages"] }),
  });
}

export function useUpdatePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stageId, data }: { stageId: string; data: Partial<Pick<PipelineStage, "name" | "position">> }) =>
      api.patch<PipelineStage>(`/pipeline/stages/${stageId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline-stages"] }),
  });
}

export function useDeletePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stageId, force = false }: { stageId: string; force?: boolean }) =>
      api.delete(`/pipeline/stages/${stageId}?force=${force}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-stages"] });
      qc.invalidateQueries({ queryKey: ["pipeline-deals"] });
    },
  });
}

export function useLossReasons() {
  return useQuery<LossReason[]>({
    queryKey: ["loss-reasons"],
    queryFn: () => api.get("/pipeline/loss-reasons"),
  });
}

export function usePipelineDeals(params: URLSearchParams) {
  const query = params.toString();
  return useQuery<PipelineDealList>({
    queryKey: ["pipeline-deals", query],
    queryFn: () => api.get(`/pipeline/deals${query ? `?${query}` : ""}`),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; phone: string; email?: string; company?: string; assigned_team_id?: string; pipeline_stage_id?: string; origin_channel?: string; referido_por?: string }) =>
      api.post<Lead>("/contacts", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["pipeline-deals"] });
      qc.invalidateQueries({ queryKey: ["pipeline-metrics"] });
    },
  });
}

export function useSetPipelineFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, followUpAt, note }: { dealId: string; followUpAt: string | null; note?: string | null }) =>
      api.patch<PipelineDeal>(`/pipeline/deals/${dealId}/follow-up`, { follow_up_at: followUpAt, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-deals"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["alerts-count"] });
    },
  });
}

export function useMovePipelineDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, stageId, loss_reason_id, loss_reason_text }: { dealId: string; stageId: string; loss_reason_id?: string; loss_reason_text?: string }) =>
      api.patch<PipelineDeal>(`/pipeline/deals/${dealId}/stage`, { stage_id: stageId, loss_reason_id, loss_reason_text }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["pipeline-deals"] });
      return {
        previousDeals: qc.getQueriesData<PipelineDealList>({ queryKey: ["pipeline-deals"] }),
      };
    },
    onError: (_error, _variables, context) => {
      context?.previousDeals.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data);
      });
    },
    onSuccess: (updatedDeal) => {
      qc.setQueriesData<PipelineDealList>({ queryKey: ["pipeline-deals"] }, (old) => {
        if (!old?.items.some((deal) => deal.id === updatedDeal.id)) return old;
        return {
          ...old,
          items: old.items.map((deal) => (deal.id === updatedDeal.id ? updatedDeal : deal)),
        };
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["pipeline-deals"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

export function useCreatePipelineNote(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string; pinned?: boolean }) => api.post<PipelineNote>(`/pipeline/deals/${dealId}/notes`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline-deals"] }),
  });
}

export function useUpdatePipelineNote(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, data }: { noteId: string; data: { content?: string; pinned?: boolean } }) =>
      api.patch<PipelineNote>(`/pipeline/deals/${dealId}/notes/${noteId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline-deals"] }),
  });
}

export function useDeletePipelineNote(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => api.delete(`/pipeline/deals/${dealId}/notes/${noteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline-deals"] }),
  });
}

export function useCreateLossReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { label: string }) => api.post<LossReason>("/pipeline/loss-reasons", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["loss-reasons"] }),
  });
}

export function useDeleteLossReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reasonId: string) => api.delete(`/pipeline/loss-reasons/${reasonId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["loss-reasons"] }),
  });
}

export interface PipelineMetrics {
  total_deals: number;
  won_deals: number;
  agent_deals: number;
  main_conversion_rate: number;
  commercial_conversion_rate: number;
}

export function usePipelineMetrics() {
  return useQuery<PipelineMetrics>({
    queryKey: ["pipeline-metrics"],
    queryFn: () => api.get("/pipeline/metrics"),
  });
}

export function useAssignDealTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, teamId }: { dealId: string; teamId: string | null }) =>
      api.patch<PipelineDeal>(`/pipeline/deals/${dealId}/team`, { assigned_team_id: teamId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipeline-deals"] }),
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { org_name: string; org_slug?: string; admin_email: string; admin_password: string; admin_full_name: string }) =>
      api.post("/platform/tenants", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform-tenants"] }),
  });
}

export function usePlatformTenants() {
  return useQuery<PlatformTenant[]>({
    queryKey: ["platform-tenants"],
    queryFn: () => api.get("/platform/tenants"),
  });
}

export function useUpdatePlatformTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, active }: { orgId: string; active: boolean }) =>
      api.patch<PlatformTenant>(`/platform/tenants/${orgId}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform-tenants"] }),
  });
}

// ── Teams ─────────────────────────────────────────────────────────────────────

export interface Team {
  id: string;
  org_id: string;
  name: string;
  is_default: boolean;
  created_at: string;
  members: { user_id: string; full_name: string | null; email: string }[];
}

export function useTeams() {
  return useQuery<Team[]>({
    queryKey: ["teams"],
    queryFn: () => api.get("/teams"),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; is_default?: boolean }) =>
      api.post<Team>("/teams", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: { name?: string; is_default?: boolean } }) =>
      api.patch<Team>(`/teams/${teamId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => api.delete(`/teams/${teamId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

export function useAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.post(`/teams/${teamId}/members/${userId}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.delete(`/teams/${teamId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

// ── Routing Rules ─────────────────────────────────────────────────────────────

export interface RoutingRule {
  id: string;
  flow_id: string;
  priority: number;
  tag_key: string;
  tag_value: string;
  team_id: string;
  created_at: string;
}

export function useRoutingRules(flowId: string) {
  return useQuery<RoutingRule[]>({
    queryKey: ["routing-rules", flowId],
    queryFn: () => api.get(`/teams/${flowId}/routing-rules`),
    enabled: !!flowId,
  });
}

export function useCreateRoutingRule(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { priority: number; tag_key: string; tag_value: string; team_id: string }) =>
      api.post<RoutingRule>(`/teams/${flowId}/routing-rules`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routing-rules", flowId] }),
  });
}

export function useDeleteRoutingRule(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.delete(`/teams/${flowId}/routing-rules/${ruleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routing-rules", flowId] }),
  });
}

// ── Channels ──────────────────────────────────────────────────────────────────

export interface Channel {
  id: string;
  flow_id: string;
  name: string;
  channel_type: string;
  ref_slug: string;
  whatsapp_connection_id: string | null;
  is_active: boolean;
  allowed_origins: string[];
  created_at: string;
  whatsapp_link: string | null;
  snippet_code: string | null;
}

export function useChannels(flowId: string) {
  return useQuery<Channel[]>({
    queryKey: ["channels", flowId],
    queryFn: () => api.get(`/flows/${flowId}/channels`),
    enabled: !!flowId,
  });
}

export function useCreateChannel(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      channel_type: string;
      whatsapp_connection_id?: string;
      allowed_origins?: string[];
    }) =>
      api.post<Channel>(`/flows/${flowId}/channels`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channels", flowId] }),
  });
}

export function useDeleteChannel(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => api.delete(`/flows/${flowId}/channels/${channelId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channels", flowId] }),
  });
}

export function useToggleChannel(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, is_active }: { channelId: string; is_active: boolean }) =>
      api.patch<Channel>(`/flows/${flowId}/channels/${channelId}`, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channels", flowId] }),
  });
}
